// src/components/assistant/MessageContent.tsx
//
// 📝 Render ÚNICO del texto de las respuestas del agente (y del razonamiento) en el
// chat: lo usan todas las variantes de ChatPane (página, Asistente a pantalla completa,
// flotante «dock», /chat). No hay una segunda implementación: si algo del Markdown del
// chat cambia, cambia aquí.
//
//  - Markdown completo con GFM (tablas, listas de tareas, tachado, autoenlaces).
//    remark-gfm se descarga aparte (perezoso, se precarga al abrir el chat): el
//    bundle inicial no crece. Mientras llega, se pinta el Markdown básico.
//  - Código: CodeBlock (lenguaje, Copiar, ajustar, contraer, diff, mermaid → diagram).
//  - Tablas con desplazamiento horizontal DENTRO de la burbuja (nunca la página).
//  - Seguridad: sin HTML crudo (sin rehype-raw: el HTML del modelo se ve como texto),
//    URLs saneadas por react-markdown (defaultUrlTransform: fuera javascript:),
//    enlaces externos target=_blank rel="noopener noreferrer".

import { Children, isValidElement, useEffect, useState, type ReactNode } from 'react';
import ReactMarkdown, { type Components, type Options } from 'react-markdown';
import { CodeBlock } from './CodeBlock';
import './messageContent.css';

type RemarkPlugins = NonNullable<Options['remarkPlugins']>;
const NO_PLUGINS: RemarkPlugins = [];
let gfmPlugins: RemarkPlugins | null = null;
let gfmPromise: Promise<void> | null = null;

/** Descarga remark-gfm (una vez). ChatPane la llama al montarse. */
export function preloadRichText(): Promise<void> {
  if (!gfmPromise) {
    gfmPromise = import('remark-gfm').then((m) => {
      gfmPlugins = [m.default];
    });
    gfmPromise.catch(() => {
      gfmPromise = null;
    });
  }
  return gfmPromise;
}

function useGfm(): RemarkPlugins {
  const [ready, setReady] = useState(!!gfmPlugins);
  useEffect(() => {
    // Pudo terminar de cargar entre el render y este efecto: sin esto se quedaba sin GFM.
    if (gfmPlugins) {
      setReady(true);
      return;
    }
    let alive = true;
    preloadRichText().then(() => alive && setReady(true)).catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);
  return ready && gfmPlugins ? gfmPlugins : NO_PLUGINS;
}

// 🧼 Sanea texto antes del Markdown para que un fragmento malformado del modelo
// (típico con razonamiento activado) no rompa el estilo del resto:
//  - quita tags <think>/<reasoning> que a veces se cuelan,
//  - balancea los code fences ``` (si quedan impares, un fence sin cerrar hace
//    que TODO lo que sigue se renderice como bloque de código → "se rompe el estilo").
export function sanitizeMarkdown(s: string | null | undefined): string {
  if (!s) return '';
  let out = s.replace(/<\/?(?:think|reasoning)\b[^>]*>/gi, '');
  const fences = (out.match(/```/g) || []).length;
  if (fences % 2 !== 0) out += '\n```';
  return out;
}

/** ¿El texto trae algo que necesita el ancho completo de la burbuja (código, tabla)? */
export function isWideMarkdown(s: string | null | undefined): boolean {
  return !!s && (/```/.test(s) || /^\s*\|.*\|\s*$/m.test(s));
}

function isExternal(href: string): boolean {
  if (!/^https?:\/\//i.test(href)) return false;
  try {
    return new URL(href).origin !== window.location.origin;
  } catch {
    return true;
  }
}

function textOf(node: ReactNode): string {
  return Children.toArray(node)
    .map((c) => (typeof c === 'string' || typeof c === 'number' ? String(c) : isValidElement<{ children?: ReactNode }>(c) ? textOf(c.props.children) : ''))
    .join('');
}

function buildComponents(live: boolean): Components {
  return {
    a: ({ node: _n, href = '', children, ...rest }) =>
      isExternal(href) ? (
        <a {...rest} href={href} target="_blank" rel="noopener noreferrer">
          {children}
        </a>
      ) : (
        <a {...rest} href={href}>
          {children}
        </a>
      ),
    // ```lang … ``` → CodeBlock. El <code> de adentro trae la clase `language-xxx`.
    pre: ({ children }) => {
      const child = Children.toArray(children)[0];
      if (!isValidElement<{ className?: string; children?: ReactNode }>(child)) return <pre>{children}</pre>;
      const lang = /language-([\w+#.-]+)/.exec(child.props.className || '')?.[1] || '';
      const code = textOf(child.props.children).replace(/\n$/, '');
      return <CodeBlock code={code} lang={lang} live={live} />;
    },
    table: ({ node: _n, children, ...rest }) => (
      <div className="o-md-table" tabIndex={0} role="region" aria-label="Tabla (desliza para ver más columnas)">
        <table {...rest}>{children}</table>
      </div>
    ),
    input: ({ node: _n, type, checked, disabled, ...rest }) =>
      type === 'checkbox' ? (
        // Solo lectura (no `disabled`: Chromium lo pinta gris y baja el contraste).
        <input {...rest} type="checkbox" checked={!!checked} readOnly tabIndex={-1} aria-readonly="true" aria-label={checked ? 'Hecho' : 'Pendiente'} />
      ) : (
        <input {...rest} type={type} disabled={disabled} />
      ),
    img: ({ node: _n, alt = '', ...rest }) => <img {...rest} alt={alt} loading="lazy" className="max-w-full rounded-xl border border-border" />,
  };
}
const STATIC_COMPONENTS = buildComponents(false);
const LIVE_COMPONENTS = buildComponents(true);

export interface MessageContentProps {
  /** Markdown de la respuesta (ya sin el sobre JSON del backend). */
  text: string;
  /** Se está escribiendo en vivo (stream): sin resaltado ni diagramas hasta el final. */
  live?: boolean;
  /** Variante discreta (razonamiento): letra chica y color atenuado. */
  muted?: boolean;
  className?: string;
}

export function MessageContent({ text, live = false, muted = false, className = '' }: MessageContentProps) {
  const remarkPlugins = useGfm();
  return (
    <div className={`o-md ${muted ? 'text-xs text-muted-foreground' : 'text-sm text-foreground'} ${className}`}>
      <ReactMarkdown remarkPlugins={remarkPlugins} components={live ? LIVE_COMPONENTS : STATIC_COMPONENTS}>
        {sanitizeMarkdown(text)}
      </ReactMarkdown>
    </div>
  );
}
